import { useEffect, useRef, useState } from "react";
import api from "../services/api";
import Swal from "sweetalert2";
import useCart from "../hooks/useCart";
import Select from "react-select";

export default function PostForm({ onSuccess }) {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [aplicaRetencion1, setAplicaRetencion1] = useState(false);

  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [cash, setCash] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tipoDocumento, setTipoDocumento] = useState("01");
  const [pendingCart, setPendingCart] = useState(null);

  const inputRef = useRef(null);

  const {
    cart,
    setCart,
    subtotal,
    addProductByCode,
    addProduct,
    updateQuantity,
    removeItem,
    clearCart
  } = useCart();

  // 🔹 Cargar clientes
  useEffect(() => {
    api.get("/clients").then(res => {
      const data = res.data.data ?? res.data;
      setClients(data);

      const varios = data.find(
        c => c.nombre.toUpperCase() === "CLIENTES VARIOS"
      );

      if (varios) {
        setSelectedClient({ value: varios.id, label: varios.nombre });
      }
    });
  }, []);

// 🔹 Leer carrito del localStorage al montar
useEffect(() => {
  const cartFromMesa = localStorage.getItem('cart_from_mesa');
  if (cartFromMesa) {
    try {
      setPendingCart(JSON.parse(cartFromMesa));
      localStorage.removeItem('cart_from_mesa');
    } catch {
      localStorage.removeItem('cart_from_mesa');
    }
  }
}, []);

// 🔹 Aplicar carrito cuando los clientes estén listos
useEffect(() => {
  if (pendingCart && clients.length > 0) {
    const { items, client_id, tipo_documento } = pendingCart;
    setCart(items);
    if (client_id) {
      const cliente = clients.find(c => c.id === client_id);
      if (cliente) setSelectedClient({ value: cliente.id, label: cliente.nombre });
    }
    if (tipo_documento) setTipoDocumento(tipo_documento);
    setPendingCart(null);
  }
}, [pendingCart, clients]);

  // 🔹 Cargar productos
  useEffect(() => {
    api.get("/products").then(res => {
      const data = res.data.data ?? res.data;
      setProducts(data);
    });
  }, []);

  // 🔹 Foco permanente
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // 🔹 Buscar mientras escribe
  useEffect(() => {
    if (!search) {
      setResults([]);
      return;
    }

    const q = search.toLowerCase();

    setResults(
      products
        .filter(p => {
          const matchCodigo = p.codigo?.toLowerCase().includes(q);
          const matchNombre = p.nombre?.toLowerCase().includes(q);
          return matchCodigo || matchNombre;
        })
        .slice(0, 6)
    );
  }, [search, products]);

  // 🔹 Actualizar tipo de documento cuando cambia el cliente
  useEffect(() => {
    if (selectedClient) {
      const cliente = clients.find(c => c.id === selectedClient.value);
      if (cliente) {
        setTipoDocumento(cliente.tipo_cliente === "Contribuyente" ? "03" : "01");
      }
    }
  }, [selectedClient, clients]);

  // 🔹 Enter (escáner)
  const handleKeyDown = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();

      if (results.length > 0) {
        addProduct(results[0]);
      } else {
        await addProductByCode(search);
      }

      setSearch("");
      setResults([]);
    }
  };

  // 🔹 Click en resultado
  const handleSelect = (product) => {
    addProduct(product);
    setSearch("");
    setResults([]);
    inputRef.current.focus();
  };

  // 🔹 Construir payload seguro
  const buildSafePayload = (clientId, cartItems, cashInput, tipoDoc, aplicaRet1 = false) => {
  const cashAmount = parseFloat(cashInput) || 0;

    const clientFull = clients.find(c => c.id === Number(clientId));
    if (!clientFull) throw new Error("Cliente no encontrado");

    const details = cartItems.map(item => ({
      product_id: Number(item.product_id),
      presentation_id: item.presentation_id || null,
      cantidad: Number(item.cantidad) || 1,
      precio_unitario: Number(item.precio_unitario) || 0
    }));

    if (details.length === 0) {
      throw new Error("El carrito debe tener al menos un producto");
    }

    const subtotal = details.reduce(
      (acc, i) => acc + i.cantidad * i.precio_unitario,
      0
    );
    const iva_total = 0;
    const total_gravado = subtotal;
    const total_exento = 0;
    const monto_final = subtotal + iva_total;

    const fechaMySQL = new Date().toISOString().slice(0, 19).replace("T", " ");

  const montoACobrar = aplicaRet1 ? monto_final - (total_gravado * 0.01) : monto_final;

  return {
      client_id: Number(clientId),
      tipo_cliente: clientFull.tipo_cliente.toLowerCase().replace(" ", "_"),
      fecha_venta: fechaMySQL,
      details,
      subtotal,
      total_gravado,
      total_exento,
      iva_total,
      monto_final,
      condicion_operacion: "CONTADO",
      estado: "PENDIENTE",
      cash: cashAmount,
      change: cashAmount - montoACobrar,
      tipo_documento: tipoDoc,
      aplica_retencion_1: aplicaRet1
    };
  };

  // 🔹 Facturar
  const submitSale = async () => {
    if (!selectedClient) {
      return Swal.fire("Error", "Seleccione un cliente", "warning");
    }

    if (cart.length === 0) {
      return Swal.fire("Error", "Carrito vacío", "warning");
    }

    const montoACobrar = aplicaRetencion1 
      ? subtotal - (subtotal * 0.01 / 1.13)
      : subtotal;

    if (Number(cash) < montoACobrar) {
      return Swal.fire("Error", "Monto insuficiente", "warning");
    }

    const clienteFull = clients.find(c => c.id === selectedClient.value);
    if (tipoDocumento === "03" && clienteFull?.tipo_cliente !== "Contribuyente") {
      return Swal.fire(
        "Error",
        "No se puede emitir Crédito Fiscal a un cliente No Contribuyente",
        "error"
      );
    }

    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const payload = buildSafePayload(selectedClient.value, cart, cash, tipoDocumento, aplicaRetencion1);
      const saleRes = await api.post("/sales", payload);
      const saleData = saleRes.data.data;

      onSuccess({
        ...saleData,
        cash: payload.cash,
        change: payload.change
      });

      clearCart();
      setCash("");
      setShowPayModal(false);

    } catch (error) {
      console.error("ERROR FACTURAR COMPLETO:", error.response?.data || error);
      Swal.fire(
        "Error",
        error.response?.data?.message || "Error al facturar",
        "error"
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientOptions = clients.map(c => ({
    value: c.id,
    label: c.nombre
  }));

  const clienteFull = selectedClient ? clients.find(c => c.id === selectedClient.value) : null;
  const puedeElegirTipoFactura = clienteFull?.tipo_cliente === "Contribuyente";

  return (
    <>
      {/* Cliente */}
      <div className="form-group">
        <label className="form-label">Cliente</label>
        <Select
          options={clientOptions}
          value={selectedClient}
          onChange={setSelectedClient}
          placeholder="Seleccionar cliente..."
          styles={{
            control: (base) => ({
              ...base,
              borderColor: '#e2e8f0',
              borderWidth: '2px',
              borderRadius: '10px',
              padding: '4px',
              '&:hover': { borderColor: '#667eea' }
            })
          }}
        />
      </div>

      {/* Input POS */}
      <div className="form-group" style={{ position: 'relative' }}>
        <label className="form-label">Buscar Producto</label>
        <input
          ref={inputRef}
          className="form-control"
          placeholder="Escanear o buscar producto..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {results.length > 0 && (
          <div style={{ position: 'absolute', width: '100%', zIndex: 1000, marginTop: '4px' }}>
            <div className="card" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {results.map(p => (
                <div
                  key={p.id}
                  onClick={() => handleSelect(p)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    borderBottom: '1px solid #e2e8f0',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#f7fafc'}
                  onMouseLeave={(e) => e.target.style.background = 'white'}
                >
                  <strong style={{ color: 'var(--primary-color)' }}>{p.codigo}</strong>
                  {' — '}
                  <span style={{ color: 'var(--text-primary)' }}>{p.nombre}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Carrito */}
      <div className="table-container mt-4">
        <table className="table">
          <thead>
            <tr>
              <th>Código</th>
              <th>Descripción</th>
              <th style={{ width: 100, textAlign: 'center' }}>Cantidad</th>
              <th style={{ width: 120, textAlign: 'right' }}>Precio Unit.</th>
              <th style={{ width: 120, textAlign: 'right' }}>Total</th>
              <th style={{ width: 80, textAlign: 'center' }}>Acción</th>
            </tr>
          </thead>
          <tbody>
            {cart.length === 0 && (
              <tr>
                <td colSpan="6" className="text-center" style={{ padding: '32px' }}>
                  <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                    El carrito está vacío. Escanea o busca productos para agregar.
                  </p>
                </td>
              </tr>
            )}
            {cart.map(p => (
              <tr key={p.uniqueKey}>
                <td>
                  <strong style={{ color: 'var(--text-primary)' }}>{p.codigo}</strong>
                </td>
                <td>{p.nombre}</td>
                <td style={{ textAlign: 'center' }}>
                  <input
                    type="number"
                    min="1"
                    className="form-control text-center"
                    value={p.cantidad}
                    onChange={e => updateQuantity(p.uniqueKey, e.target.value)}
                    style={{ padding: '8px' }}
                  />
                </td>
                <td style={{ textAlign: 'right' }}>${p.precio_unitario.toFixed(2)}</td>
                <td style={{ textAlign: 'right' }}>
                  <strong style={{ color: 'var(--primary-color)' }}>
                    ${p.subtotal.toFixed(2)}
                  </strong>
                </td>
                <td className="text-center">
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => removeItem(p.uniqueKey)}
                    title="Eliminar producto"
                  >
                    ✕
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Total */}
      <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
        <h3 style={{ color: 'var(--text-primary)', margin: 0 }}>Total a pagar:</h3>
        <h2 style={{ color: 'var(--primary-color)', margin: 0, fontSize: '32px', fontWeight: '700' }}>
          ${subtotal.toFixed(2)}
        </h2>
      </div>

      <button
        className="btn btn-success btn-lg"
        disabled={cart.length === 0}
        onClick={() => setShowPayModal(true)}
        style={{ width: '100%' }}
      >
        💳 Cobrar / Facturar
      </button>

      {/* Modal Cobro */}
      {showPayModal && (
        <>
          <div className="modal fade show d-block">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content card">

                <div className="card-header">
                  <h3 className="card-title" style={{ margin: 0 }}>💰 Cobro</h3>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => { setShowPayModal(false); setCash(""); setAplicaRetencion1(false); }}
                  >
                    ✕
                  </button>
                </div>

                <div className="card-body">
                  <div className="alert alert-info mb-3">
                    <strong>Cliente:</strong> {clienteFull?.nombre || "No seleccionado"}
                    {clienteFull && (
                      <span style={{ marginLeft: '8px', fontSize: '13px', color: '#0369a1' }}>
                        ({clienteFull.tipo_cliente})
                      </span>
                    )}
                  </div>

                  <div className="form-group mb-3">
                    <label className="form-label" style={{ fontWeight: 600 }}>Tipo de Factura</label>

                    {!puedeElegirTipoFactura ? (
                      <div style={{ padding: '12px', background: '#f0f9ff', borderRadius: '8px', border: '2px solid #0ea5e9' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <input type="radio" checked disabled />
                          <span style={{ fontWeight: 600 }}>📄 Consumidor Final</span>
                        </div>
                        <small style={{ color: '#0369a1', display: 'block', marginTop: '4px' }}>
                          Este cliente solo puede recibir factura de consumidor final
                        </small>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div
                          onClick={() => setTipoDocumento("01")}
                          style={{
                            padding: '12px',
                            background: tipoDocumento === "01" ? '#f0f9ff' : '#f7fafc',
                            border: tipoDocumento === "01" ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                            borderRadius: '8px', cursor: 'pointer'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" checked={tipoDocumento === "01"} onChange={() => setTipoDocumento("01")} />
                            <span style={{ fontWeight: 600 }}>📄 Consumidor Final</span>
                          </div>
                        </div>

                        <div
                          onClick={() => setTipoDocumento("03")}
                          style={{
                            padding: '12px',
                            background: tipoDocumento === "03" ? '#f0fdf4' : '#f7fafc',
                            border: tipoDocumento === "03" ? '2px solid #10b981' : '1px solid #e2e8f0',
                            borderRadius: '8px', cursor: 'pointer'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <input type="radio" checked={tipoDocumento === "03"} onChange={() => setTipoDocumento("03")} />
                            <span style={{ fontWeight: 600 }}>💼 Crédito Fiscal</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Checkbox Retención 1% - Visible para ambos tipos */}
                <div className="form-group mb-3" style={{
                  padding: '12px',
                  background: '#fffbeb',
                  borderRadius: '8px',
                  border: '1px solid #f59e0b'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="checkbox"
                      id="aplicaRetencion"
                      checked={aplicaRetencion1}
                      onChange={e => setAplicaRetencion1(e.target.checked)}
                      style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <label htmlFor="aplicaRetencion" style={{ cursor: 'pointer', fontWeight: 600, margin: 0 }}>
                      Aplicar Retención 1% IVA
                    </label>
                  </div>
                  {aplicaRetencion1 && (
                    <small style={{ color: '#92400e', display: 'block', marginTop: '6px' }}>
                      Retención: ${(subtotal * 0.01 / 1.13).toFixed(4)} — 
                      Neto a cobrar: ${(subtotal - (subtotal * 0.01 / 1.13)).toFixed(2)}
                    </small>
                  )}
                </div>

                <div className="alert alert-success">
                  <strong>Total a pagar:</strong> ${aplicaRetencion1 
                    ? (subtotal - (subtotal * 0.01 / 1.13)).toFixed(2)
                    : subtotal.toFixed(2)}
                </div>

                  <div className="form-group">
                    <label className="form-label">Monto Recibido</label>
                    <input
                      type="text"
                      inputMode="decimal"
                      className="form-control"
                      placeholder="0.00"
                      value={cash}
                      autoFocus
                      onChange={e => {
                        const value = e.target.value;
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setCash(value);
                        }
                      }}
                      onKeyDown={e => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          const montoNet = aplicaRetencion1 ? subtotal - (subtotal * 0.01 / 1.13) : subtotal;
                          if (cash && Number(cash) >= montoNet && !isSubmitting) {
                            submitSale();
                          }
                        }
                      }}
                      style={{ fontSize: '18px', fontWeight: '600' }}
                    />
                  </div>

                  {cash !== "" && (
                    <div className={`alert ${
                      Number(cash) >= (aplicaRetencion1 ? subtotal - (subtotal * 0.01 / 1.13) : subtotal)
                        ? "alert-success"
                        : "alert-danger"
                    } mt-3`}>
                      <strong>
                        {Number(cash) >= (aplicaRetencion1 ? subtotal - (subtotal * 0.01 / 1.13) : subtotal)
                          ? `✓ Vuelto: $${(Number(cash) - (aplicaRetencion1 ? subtotal - (subtotal * 0.01 / 1.13) : subtotal)).toFixed(2)}`
                          : "⚠ Monto insuficiente"}
                      </strong>
                    </div>
                  )}
                </div>

                <div className="card-body" style={{ borderTop: '2px solid #e2e8f0', display: 'flex', gap: '12px', paddingTop: 'var(--spacing-md)' }}>
                  <button
                    className="btn btn-secondary"
                    onClick={() => { setShowPayModal(false); setCash(""); setAplicaRetencion1(false); }}
                    style={{ flex: 1 }}
                  >
                    Cancelar
                  </button>

                  <button
                    className="btn btn-success"
                    disabled={!cash || Number(cash) < (aplicaRetencion1 ? subtotal - (subtotal * 0.01 / 1.13) : subtotal) || isSubmitting}
                    onClick={submitSale}
                    style={{ flex: 1 }}
                  >
                    {isSubmitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Procesando...
                      </>
                    ) : '✓ Facturar'}
                  </button>
                </div>

              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </>
  );
}